/**
 * Supported-file-types BY-KIND bubbles — the SHARED single source of truth for
 * the upload-target "what DocAble supports" display across BOTH the main SPA
 * (`web/static/index.html`) and the `/editor` empty state (`web/static/editor/`).
 *
 * This module lives at `web/static/shared/` (the cross-tree shared tier,
 * mirroring `shared/role-palette.ts` / `shared/editor-routing.ts`) because it
 * has two consumers (rule #11 / A.6). It emits as ESM via
 * `web/static/tsconfig.esm-modules.emit.json` and is served at
 * `/static/shared/supported-format-bubbles.js`.
 *
 * The GROUPING DATA is NOT owned here — it is a pure projection of
 * `ADVERTISED_BY_KIND` from the codegen'd `../generated/advertised-formats.ts`
 * (itself emitted from `system-models/supported_filetypes.py::ADVERTISED_FORMATS`
 * by `tools/build/gen_advertised_formats.py`). So the four bubbles
 * (Documents / Presentations / Spreadsheets / PDF) and the formats inside each
 * are defined in ONE place; adding a format or flipping a kind updates both
 * trees automatically (D1 "change one site",
 * frontend-knobs-and-visuals-redesign-260901).
 *
 * This module owns only the DOM RENDER of that data. Pure builder: it imports
 * only the (side-effect-free) generated data module, so it is independently
 * unit-testable in jsdom.
 *
 * @module supported-format-bubbles
 */
import { ADVERTISED_BY_KIND } from "../generated/advertised-formats.js";
const SVG_NS = "http://www.w3.org/2000/svg";
const XLINK_NS = "http://www.w3.org/1999/xlink";
/** Build one format chip: a decorative glyph + its display name label. */
function _formatChip(fmt) {
    const chip = document.createElement("span");
    chip.className = "format-chip";
    chip.dataset.format = fmt.formatName;
    const svg = document.createElementNS(SVG_NS, "svg");
    svg.setAttribute("width", "26");
    svg.setAttribute("height", "26");
    svg.setAttribute("viewBox", "0 0 32 32");
    svg.setAttribute("aria-hidden", "true");
    const use = document.createElementNS(SVG_NS, "use");
    // Both `href` (modern) and `xlink:href` (Safari fallback), mirroring the
    // rest of the page's `<use>` sites.
    use.setAttribute("href", `#${fmt.iconId}`);
    use.setAttributeNS(XLINK_NS, "xlink:href", `#${fmt.iconId}`);
    svg.appendChild(use);
    const label = document.createElement("span");
    label.className = "format-chip-label";
    label.textContent = fmt.displayName;
    chip.appendChild(svg);
    chip.appendChild(label);
    return chip;
}
/** Build one by-kind bubble: a heading + the format chips inside it. */
function _bubble(group) {
    const bubble = document.createElement("div");
    bubble.className = "format-bubble";
    bubble.dataset.kind = group.kind;
    bubble.setAttribute("role", "group");
    bubble.setAttribute("aria-label", group.label);
    const heading = document.createElement("span");
    heading.className = "format-bubble-label";
    heading.textContent = group.label;
    bubble.appendChild(heading);
    const chips = document.createElement("span");
    chips.className = "format-bubble-chips";
    for (const fmt of group.formats) {
        chips.appendChild(_formatChip(fmt));
    }
    bubble.appendChild(chips);
    return bubble;
}
/**
 * Render the by-kind supported-format bubbles into *container*.
 *
 * Idempotent — clears and re-fills, so a re-call (hot reload) doesn't duplicate.
 * The bubbles are labelled by kind; each format glyph is decorative
 * (`aria-hidden`) because the chip's text label carries the accessible name.
 */
export function renderSupportedFormatBubbles(container) {
    container.replaceChildren(...ADVERTISED_BY_KIND.map(_bubble));
}
/**
 * Convenience wrapper: render into the element with *id*, if present. No-ops on
 * a missing container (defensive for partial DOM); the served-page smoke gate is
 * the real guard that the container exists.
 */
export function renderSupportedFormatBubblesInto(id) {
    const el = document.getElementById(id);
    if (el)
        renderSupportedFormatBubbles(el);
}
